import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, MessageSquare, Search } from "lucide-react";

const TYPE_LABEL = { bug: "Bug", tweak: "Tweak", improvement: "Improvement", design: "Design" };
const STATUS_LABEL = {
  new: "New", under_review: "Under Review", approved: "Approved", in_progress: "In Progress",
  ready_for_testing: "Ready for Testing", resolved: "Resolved", closed: "Closed", rejected: "Rejected",
};
export const STATUS_COL = {
  new: "bg-slate-100 text-slate-700", under_review: "bg-blue-100 text-blue-700", approved: "bg-violet-100 text-violet-700",
  in_progress: "bg-amber-100 text-amber-700", ready_for_testing: "bg-cyan-100 text-cyan-700",
  resolved: "bg-emerald-100 text-emerald-700", closed: "bg-slate-200 text-slate-600", rejected: "bg-rose-100 text-rose-700",
};
export const PRIO_COL = { low: "bg-slate-100 text-slate-600", medium: "bg-amber-100 text-amber-700", high: "bg-rose-100 text-rose-700" };
const dstr = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

function Card({ label, value, tone }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`fb-card-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className={`text-2xl font-bold ${tone}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

export default function FeedbackAdmin() {
  const [meta, setMeta] = useState(null);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState(null);
  const [f, setF] = useState({ type: "", priority: "", status: "", assigned_to_id: "", q: "" });

  const load = () => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/feedback", { params }).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
    api.get("/feedback/summary").then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { api.get("/feedback/meta").then((r) => setMeta(r.data)).catch(() => setMeta({ is_manager: false, assignees: [] })); }, []);
  useEffect(() => { const t = setTimeout(load, 150); return () => clearTimeout(t); }, [f]); // eslint-disable-line

  const mgr = meta?.is_manager;
  const Sel = ({ k, options, ph }) => (
    <select value={f[k]} onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))} data-testid={`fb-filter-${k}`}
      className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700">
      <option value="">{ph}</option>
      {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );

  return (
    <div className="max-w-6xl" data-testid="feedback-admin">
      <div className="flex items-center gap-3 mb-1">
        <span className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><MessageSquare className="w-5 h-5" /></span>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Feedback &amp; Bugs</h1>
          <p className="text-sm text-slate-500">Tester submissions — triage, assign and track to resolution.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-5">
        <Card label="Open" value={summary?.open ?? "—"} tone="text-slate-900" />
        <Card label="In Progress" value={summary?.in_progress ?? "—"} tone="text-amber-600" />
        <Card label="Ready for Testing" value={summary?.ready_for_testing ?? "—"} tone="text-cyan-600" />
        <Card label="Resolved" value={summary?.resolved ?? "—"} tone="text-emerald-600" />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={f.q} onChange={(e) => setF((p) => ({ ...p, q: e.target.value }))} placeholder="Search…" data-testid="fb-filter-q"
            className="h-9 pl-8 pr-3 rounded-lg border border-slate-200 text-sm w-52" />
        </div>
        <Sel k="type" ph="All types" options={Object.entries(TYPE_LABEL).map(([v, l]) => ({ v, l }))} />
        <Sel k="priority" ph="All priorities" options={["low", "medium", "high"].map((v) => ({ v, l: v[0].toUpperCase() + v.slice(1) }))} />
        <Sel k="status" ph="All statuses" options={Object.entries(STATUS_LABEL).map(([v, l]) => ({ v, l }))} />
        {mgr && <Sel k="assigned_to_id" ph="Anyone" options={(meta?.assignees || []).map((a) => ({ v: a.id, l: a.name }))} />}
      </div>

      {items === null ? (
        <div className="flex items-center gap-2 text-slate-500 py-8"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-500" data-testid="fb-empty">No feedback matches these filters.</div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-xl">
          <table className="w-full text-sm" data-testid="fb-table">
            <thead className="bg-slate-50 text-slate-500 text-left text-xs uppercase tracking-wide">
              <tr>{["Ref", "Title", "Type", "Priority", "Status", "Submitted by", "Date", "Assigned to"].map((h) => <th key={h} className="px-3 py-2.5 font-semibold whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50" data-testid={`fb-row-${t.ref}`}>
                  <td className="px-3 py-2.5 font-mono text-xs whitespace-nowrap"><Link to={`/docs/feedback/${t.id}`} className="text-indigo-600 hover:underline">{t.ref}</Link></td>
                  <td className="px-3 py-2.5"><Link to={`/docs/feedback/${t.id}`} className="text-slate-800 hover:text-indigo-600 font-medium">{t.title}</Link></td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{TYPE_LABEL[t.type] || t.type}</td>
                  <td className="px-3 py-2.5"><span className={`text-[11px] font-semibold rounded-md px-2 py-0.5 capitalize ${PRIO_COL[t.priority]}`}>{t.priority}</span></td>
                  <td className="px-3 py-2.5"><span className={`text-[11px] font-semibold rounded-md px-2 py-0.5 ${STATUS_COL[t.status]}`}>{STATUS_LABEL[t.status]}</span></td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{t.submitted_by?.name || "—"}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-500">{dstr(t.created_at)}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{t.assigned_to?.name || <span className="text-slate-300">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
